import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth'

export interface User {
  id: string
  name: string
  email: string
  email_verified_at: string | null
  created_at: string
  updated_at: string
}

export interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  const initializeAuth = async (): Promise<void> => {
    // Prevent duplicate initialization calls
    if (isLoading.value || isAuthenticated.value) {
      console.log('Auth already loading or initialized, skipping duplicate call...')
      return
    }

    isLoading.value = true
    console.log('Initializing authentication...')

    try {
      // Check for existing token in localStorage
      const storedToken = localStorage.getItem('auth_token')
      const storedUser = localStorage.getItem('user_data')

      if (storedToken && storedUser) {
        try {
          token.value = storedToken
          user.value = JSON.parse(storedUser)
          console.log('Restored auth state from localStorage for:', user.value?.email)
          
          // Verify token is still valid
          await verifyToken()
        } catch (err) {
          console.error('Failed to restore auth state:', err)
          clearAuth()
        }
      } else {
        console.log('No stored auth token found')
      }
    } finally {
      isLoading.value = false
    }
  }

  const login = async (email: string, password: string): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.login(email, password)
      
      if (response.success && response.user && response.token) {
        user.value = response.user
        token.value = response.token
        
        // Store in localStorage for persistence
        localStorage.setItem('auth_token', response.token)
        localStorage.setItem('user_data', JSON.stringify(response.user))
        
        return true
      } else {
        error.value = response.message || 'Login failed'
        return false
      }
    } catch (err: any) {
      error.value = err.message || 'Login failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    isLoading.value = true
    
    try {
      if (token.value) {
        await authService.logout()
      }
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearAuth()
      isLoading.value = false
    }
  }

  const register = async (name: string, email: string, password: string): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.register(name, email, password)
      
      if (response.success && response.user && response.token) {
        user.value = response.user
        token.value = response.token
        
        // Store in localStorage for persistence
        localStorage.setItem('auth_token', response.token)
        localStorage.setItem('user_data', JSON.stringify(response.user))
        
        return true
      } else {
        error.value = response.message || 'Registration failed'
        return false
      }
    } catch (err: any) {
      error.value = err.message || 'Registration failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const verifyToken = async (): Promise<boolean> => {
    if (!token.value) return false

    try {
      const response = await authService.verifyToken()
      
      if (response.success && response.user) {
        user.value = response.user
        localStorage.setItem('user_data', JSON.stringify(response.user))
        return true
      } else {
        clearAuth()
        return false
      }
    } catch (err) {
      clearAuth()
      return false
    }
  }

  const refreshUser = async (): Promise<boolean> => {
    if (!isAuthenticated.value) return false

    try {
      const response = await authService.getCurrentUser()
      
      if (response.success && response.user) {
        user.value = response.user
        localStorage.setItem('user_data', JSON.stringify(response.user))
        return true
      }
      return false
    } catch (err) {
      console.error('Failed to refresh user data:', err)
      return false
    }
  }

  const clearAuth = (): void => {
    user.value = null
    token.value = null
    error.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
  }

  const clearError = (): void => {
    error.value = null
  }

  return {
    // State
    user, token, isLoading, error,
    // Getters
    isAuthenticated, userName, userEmail,
    // Actions
    initializeAuth, login, logout, register, verifyToken, refreshUser, clearAuth, clearError
  }
})